import { useEffect, useState } from 'react';
import { Button, Form, InputNumber, Space, Spin, Typography, message } from 'antd';
import { ReloadOutlined, SaveOutlined, SettingOutlined } from '@ant-design/icons';
import AppCard from '../../../components/cards/AppCard';
import { getPOMsaConfig, savePOMsaConfig } from '../../../services/adminApi';
import '../../../styles/FormGroupsSection.css';

const { Text } = Typography;

const DEFAULTS = {
  employerMsaValidityDays: 365,
  referralMsaValidityDays: 365,
  expiryWarningDays: 30,
};

export default function POMsaSettingsSection() {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const data = await getPOMsaConfig();
      form.setFieldsValue({ ...DEFAULTS, ...(data || {}) });
    } catch (error) {
      message.error(error?.message || 'Could not load PO/MSA settings');
      form.setFieldsValue(DEFAULTS);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const save = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);
      const data = await savePOMsaConfig(values);
      form.setFieldsValue({ ...DEFAULTS, ...(data || values) });
      message.success('PO/MSA validity settings saved');
    } catch (error) {
      if (!error?.errorFields) message.error(error?.message || 'Could not save PO/MSA settings');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fg-section admin-config-section">
      <AppCard
        icon={<SettingOutlined />}
        title="PO/MSA Validity Settings"
        extra={(
          <Space>
            <Button icon={<ReloadOutlined />} onClick={load} disabled={saving}>Reload</Button>
            <Button type="primary" icon={<SaveOutlined />} loading={saving} onClick={save}>Save</Button>
          </Space>
        )}
      >
        <div className="admin-accent-bar" />
        <Text type="secondary">
          Configure how long a signed MSA remains valid. Employer/referral status is calculated from the signed MSA date plus these values; no validity period is hardcoded in the onboarding UI.
        </Text>
      </AppCard>

      <AppCard title="Validity Rules">
        <Spin spinning={loading}>
          <Form form={form} layout="vertical" initialValues={DEFAULTS} style={{ maxWidth: 620 }}>
            <Form.Item
              name="employerMsaValidityDays"
              label="Employer MSA Validity Days"
              tooltip="Number of days an employer MSA remains active from its signed date."
              rules={[{ required: true, message: 'Employer MSA validity is required' }]}
            >
              <InputNumber min={1} max={3650} precision={0} style={{ width: '100%' }} addonAfter="days" />
            </Form.Item>
            <Form.Item
              name="referralMsaValidityDays"
              label="Referral MSA Validity Days"
              tooltip="Number of days a referral MSA remains active from its signed date."
              rules={[{ required: true, message: 'Referral MSA validity is required' }]}
            >
              <InputNumber min={1} max={3650} precision={0} style={{ width: '100%' }} addonAfter="days" />
            </Form.Item>
            <Form.Item
              name="expiryWarningDays"
              label="Expiry Warning Days"
              tooltip="Reserved for upcoming-expiry indicators and notifications."
              rules={[{ required: true, message: 'Expiry warning days is required' }]}
            >
              <InputNumber min={0} max={3650} precision={0} style={{ width: '100%' }} addonAfter="days" />
            </Form.Item>
          </Form>
        </Spin>
      </AppCard>
    </div>
  );
}
